/**
 * SourceChip — inline provenance affordance for an extracted field.
 *
 * Given a target object (CLIN by clin_number, program by contract_number, …)
 * and a field path, it finds the persisted source_reference for that field and
 * renders a small info trigger; the popover shows page / section / quote /
 * confidence — where the value came from in the source PDF.
 *
 * Masking-aware and fail-CLOSED: the source quote/raw value IS the underlying
 * number, so if the caller marks the field sensitive (resource + maskFieldName)
 * and the viewer's projection masks it, the chip renders NOTHING — surfacing a
 * quote like "…$2,369,660…" next to a redacted cell would leak it. Renders
 * nothing too when there's no ref for the field (the common case until SP-1
 * provenance has been ingested for a program).
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { FileSearch } from 'lucide-react';
import { useState } from 'react';
import { DocumentViewerDrawer } from '../../../../components/ProposalDashboard/DocumentViewer';
import { useArtifactNames } from '../../../../hooks/useDeliveryDocuments';
import { fieldIsMasked } from '../../../../lib/access/types';
import { useViewProjection } from '../../../../lib/access/useViewProjection';
import { useSourceRefsForTarget } from '../hooks/lineageAuditHooks';
import { readableSnippet } from '../lib/readableSnippet';
import { parseSpan } from '../lib/sourceSpan';
export function SourceChip(props) {
    if (props.resource && props.maskFieldName) {
        return /*#__PURE__*/ _jsx(GuardedSourceChip, {
            ...props,
            resource: props.resource,
            maskFieldName: props.maskFieldName
        });
    }
    return /*#__PURE__*/ _jsx(SourceChipContent, {
        ...props
    });
}
function GuardedSourceChip(props) {
    const { data: policy } = useViewProjection(props.resource);
    // A source quote can reveal the masked value: do not even fetch refs until allowed.
    if (!policy || fieldIsMasked(policy.viewMode, policy.maskedFields, props.maskFieldName)) return null;
    return /*#__PURE__*/ _jsx(SourceChipContent, {
        ...props
    });
}
function SourceChipContent({ targetObjectType, targetObjectId, fallbackTargetObjectId, fieldPath, projectId, viewerMode = 'standalone', parentDrawerWidth }) {
    const [viewerOpen, setViewerOpen] = useState(false);
    const { data: primaryRefs, isError: primaryErrored } = useSourceRefsForTarget({
        targetObjectType,
        targetObjectId
    });
    const { data: fallbackRefs } = useSourceRefsForTarget({
        targetObjectType,
        targetObjectId: fallbackTargetObjectId ?? '',
        // Sequential, not parallel: `primaryRefs === undefined` means the primary
        // is still loading — firing then would issue two RPCs per row even when
        // the primary resolves (and could transiently render the fallback ref).
        // An ERRORED primary leaves data undefined forever, so it must count as
        // settled-empty or the chip vanishes instead of degrading to the CDRL
        // key. A blank primary targetObjectId (e.g. no task number) disables the
        // primary hook outright, so `primaryRefs` would otherwise stay undefined
        // forever too -- treat it as settled-empty from the start.
        enabled: !!fallbackTargetObjectId && (primaryErrored || !targetObjectId || primaryRefs !== undefined && primaryRefs.length === 0)
    });
    const refs = primaryRefs?.length ? primaryRefs : fallbackRefs;
    const ref = fieldPath ? refs?.find((r)=>r.targetFieldPath === fieldPath) : refs?.[0];
    // Resolve the real artifact filename (never the source_kind token, e.g.
    // "FinalContract" — that's a provenance category, not a document name) for
    // the viewer header. Called unconditionally (before the masked/no-ref
    // early return) so hook order never depends on that branch.
    const fileNameById = useArtifactNames(ref?.sourceArtifactId ? [
        ref.sourceArtifactId
    ] : []);
    if (!ref) return null;
    // Deliverable-definition refs carry the page only inside the block span.
    const span = parseSpan(ref.sourceSpan);
    const openPage = ref.sourcePage > 0 ? ref.sourcePage : span.page ?? 1;
    const resolvedFilename = ref.sourceArtifactId ? fileNameById.get(ref.sourceArtifactId) ?? 'Unknown document' : 'Unknown document';
    // Origin should read as a filename, not baltigo's source_kind category
    // (e.g. "FinalContract" — meaningless to a user). Omit the row entirely
    // until the artifact name has actually resolved rather than flashing
    // "Unknown document" while the per-id query is still in flight.
    const originFilename = ref.sourceArtifactId ? fileNameById.get(ref.sourceArtifactId) : undefined;
    const confidencePct = ref.confidence > 0 ? `${Math.round(ref.confidence * 100)}%` : null;
    return /*#__PURE__*/ _jsxs(Popover, {
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    className: "ml-1 inline-flex items-center align-middle text-muted-foreground hover:text-foreground",
                    "aria-label": `Source for ${fieldPath ?? targetObjectType}`,
                    title: "View source",
                    "data-testid": Testids.deliveryShared.sourceChip,
                    children: /*#__PURE__*/ _jsx(FileSearch, {
                        className: "h-3 w-3",
                        "aria-hidden": true
                    })
                })
            }),
            /*#__PURE__*/ _jsxs(PopoverContent, {
                className: "w-80 text-sm",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mb-2 flex items-center justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: "Source"
                            }),
                            confidencePct && /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    "confidence ",
                                    confidencePct
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("dl", {
                        className: "space-y-1",
                        children: [
                            ref.sourcePage > 0 && /*#__PURE__*/ _jsx(Row, {
                                k: "Page",
                                v: `p.${ref.sourcePage}`
                            }),
                            ref.sourceSection && /*#__PURE__*/ _jsx(Row, {
                                k: "Section",
                                v: ref.sourceSection
                            }),
                            originFilename && /*#__PURE__*/ _jsx(Row, {
                                k: "Origin",
                                v: originFilename,
                                testid: Testids.deliveryShared.sourceChipOrigin
                            })
                        ]
                    }),
                    ref.sourceQuote && /*#__PURE__*/ _jsxs("blockquote", {
                        className: "mt-2 whitespace-pre-line border-l-2 border-muted pl-2 text-xs italic text-muted-foreground",
                        children: [
                            "“",
                            readableSnippet(ref.sourceQuote),
                            "”"
                        ]
                    }),
                    ref.extractorName && /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-[10px] uppercase tracking-wide text-muted-foreground",
                        children: ref.extractorName
                    }),
                    projectId && ref.sourceArtifactId && /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        className: "mt-2 w-full rounded border px-2 py-1 text-xs font-medium text-primary hover:bg-muted",
                        onClick: ()=>setViewerOpen(true),
                        "data-testid": Testids.deliveryShared.sourceChipOpen,
                        children: [
                            "Open in document",
                            openPage > 1 || ref.sourcePage > 0 ? ` (p.${openPage})` : ''
                        ]
                    })
                ]
            }),
            projectId && ref.sourceArtifactId && viewerOpen && /*#__PURE__*/ _jsx("span", {
                onClick: (e)=>e.stopPropagation(),
                children: /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                    isOpen: true,
                    onClose: ()=>setViewerOpen(false),
                    projectId: projectId,
                    document: {
                        documentId: ref.sourceArtifactId,
                        filename: resolvedFilename
                    },
                    initialPage: openPage,
                    highlightBlockId: span.blockId,
                    mode: viewerMode,
                    ...parentDrawerWidth !== undefined && {
                        parentDrawerWidth
                    }
                })
            })
        ]
    });
}
function Row({ k, v, testid }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex justify-between gap-3",
        children: [
            /*#__PURE__*/ _jsx("dt", {
                className: "text-muted-foreground",
                children: k
            }),
            /*#__PURE__*/ _jsx("dd", {
                className: "truncate text-right",
                "data-testid": testid,
                children: v
            })
        ]
    });
}
